import { useCallback, useEffect, useRef, useState } from "react";
import { API, getWithRetry } from "@/lib/constants";

// Per-asset feed fetcher with the shared transient-retry helper. The upstream
// /assets/{id}/{addresses,transactions} endpoints are slow and routinely 503
// for high-volume tokens, so each profile section loads INDEPENDENTLY and
// degrades to a retryable "busy" state instead of breaking the page. History
// is cheap and reliable. Never fabricates data.
//
// Perf: results are cached in-session (module Map) so re-opening an asset is
// instant; retries are kept to ONE (a slow-but-eventually-200 endpoint only
// gets slower if we retry it) and a `slow` flag flips after a few seconds so
// the UI can explain the wait instead of spinning forever.
const feedCache = new Map();

export const useAssetFeed = (asset, path, params = {}, enabled = true) => {
  const key = JSON.stringify(params);
  const ck = `${asset}${path}?${key}`;
  const [state, setState] = useState(() => {
    const hit = asset ? feedCache.get(ck) : null;
    return hit !== undefined && hit !== null
      ? { data: hit, loading: false, error: null, slow: false }
      : { data: null, loading: enabled, error: null, slow: false };
  });
  const reqRef = useRef(0);
  const slowRef = useRef(null);

  const load = useCallback(async (force = false) => {
    if (!asset || !enabled) return;
    const hit = feedCache.get(ck);
    if (hit !== undefined && hit !== null && !force) {
      setState({ data: hit, loading: false, error: null, slow: false });
      return;
    }
    const my = ++reqRef.current;
    setState((s) => ({ ...s, loading: true, error: null, slow: false }));
    clearTimeout(slowRef.current);
    slowRef.current = setTimeout(() => {
      if (my === reqRef.current) setState((s) => (s.loading ? { ...s, slow: true } : s));
    }, 5000);
    try {
      const res = await getWithRetry(
        `${API}/assets/${asset}${path}`,
        { params, timeout: 20000 },
        { retries: 1, baseDelay: 1500, maxDelay: 4000 },
      );
      if (my !== reqRef.current) return;
      feedCache.set(ck, res.data);
      setState({ data: res.data, loading: false, error: null, slow: false });
    } catch (e) {
      if (my !== reqRef.current) return;
      const d = e.response?.data;
      const unavailable = !e.response || [429, 500, 502, 503, 504].includes(e.response.status);
      setState({
        data: null,
        loading: false,
        slow: false,
        error: {
          message: unavailable
            ? "The data source is busy right now — this endpoint is heavily rate-limited for high-activity tokens."
            : d?.message || e.message || "Request failed.",
          kind: unavailable ? "unavailable" : "error",
        },
      });
    } finally {
      clearTimeout(slowRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, path, enabled, key, ck]);

  useEffect(() => {
    load();
    return () => clearTimeout(slowRef.current);
  }, [load]);
  return { ...state, reload: () => load(true) };
};

const DAY = 86400;

// Build a transaction-volume time series (tx count per bucket) from the loaded
// transaction list — same spirit as the home-page volume chart but scoped to
// one asset. Honest: only the fetched (most-recent) transactions are bucketed.
export const buildVolumeSeries = (txs, windowKey) => {
  const times = (txs || [])
    .map((t) => Number(t.block_time))
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
  if (!times.length) return { points: [], bucket: "day", total: 0 };

  const now = Math.floor(Date.now() / 1000);
  let step;
  let span;
  if (windowKey === "24H") { step = 3600; span = 24 * 3600; }
  else if (windowKey === "7D") { step = DAY; span = 7 * DAY; }
  else if (windowKey === "30D") { step = DAY; span = 30 * DAY; }
  else {
    step = DAY;
    const days = Math.ceil((now - times[0]) / DAY) + 1;
    span = Math.min(Math.max(days, 1), 120) * DAY;
  }

  const start = Math.floor((now - span) / step) * step;
  const nb = Math.max(1, Math.round(span / step));
  const buckets = Array.from({ length: nb }, (_, i) => ({ t: start + i * step, count: 0 }));
  let total = 0;
  times.forEach((tm) => {
    if (tm < start) return;
    const idx = Math.min(nb - 1, Math.floor((tm - start) / step));
    if (idx >= 0) { buckets[idx].count += 1; total += 1; }
  });
  return { points: buckets, bucket: step >= DAY ? "day" : "hour", total };
};

// Cumulative supply timeline from mint/burn history (ascending), using BigInt
// so huge quantities keep precision.
export const buildSupplyTimeline = (history) => {
  const events = (history || [])
    .filter((e) => e && e.block_time)
    .slice()
    .sort((a, b) => a.block_time - b.block_time);
  if (!events.length) return [];
  let cum = 0n;
  return events.map((e) => {
    let amt = 0n;
    try { amt = BigInt(e.amount || 0); } catch { amt = 0n; }
    cum = e.action === "burned" ? cum - amt : cum + amt;
    return { t: e.block_time, supply: Number(cum), action: e.action, height: e.block_height };
  });
};
